import { createFileRoute } from "@tanstack/react-router";
import { SimpleLegalLayout, SimpleLegalSection } from "@/components/legal/SimpleLegalLayout";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    title: "privacy policy - clove.moe",
    meta: [
      {
        name: "description",
        content:
          "privacy policy describing how clove.moe collects, uses, discloses, and protects your personal data.",
      },
      { property: "og:title", content: "privacy policy - clove.moe" },
      {
        property: "og:description",
        content: "how clove.moe collects, uses, and protects your personal data",
      },
    ],
  }),
  component: PrivacyPolicyPage,
});

export function PrivacyPolicyPage() {
  return (
    <SimpleLegalLayout
      title="Privacy Policy"
      subtitle="Last updated September 15, 2025"
    >
      <SimpleLegalSection title="1. Introduction">
        <p>
          This Privacy Policy ("Policy") explains how Clove.moe ("Clove.moe,"
          "we," "us," or "our") collects, uses, discloses, and safeguards
          personal data in connection with the Clove.moe website and services
          (collectively, the "Service"). We are committed to data minimization
          and do not sell personal data. By using the Service, you consent to
          the practices described herein. If you have any questions, please
          contact us using the information in Section 11.
        </p>
      </SimpleLegalSection>

      <SimpleLegalSection title="2. Information We Collect">
        <h3 className="font-medium text-foreground mt-2">Account Information</h3>
        <ul className="list-disc pl-5 space-y-1">
          <li>Email address, used for account creation and security notices;</li>
          <li>Username (a public identifier) and any optional display name; and</li>
          <li>Profile image you upload.</li>
        </ul>
        <h3 className="font-medium text-foreground mt-4">Profile Content</h3>
        <ul className="list-disc pl-5 space-y-1">
          <li>Bio markup you create (BML or HTML/CSS);</li>
          <li>Media files you upload, including images and fonts; and</li>
          <li>Styling preferences applied to your profile page.</li>
        </ul>
        <h3 className="font-medium text-foreground mt-4">Technical Data</h3>
        <ul className="list-disc pl-5 space-y-1">
          <li>IP addresses that are hashed solely for counting unique page views; and</li>
          <li>Session cookies and minimal authentication metadata.</li>
        </ul>
        <div className="rounded-md border bg-muted/40 px-3 py-2 text-xs">
          <strong>We do not collect</strong> precise geolocation, behavioral
          advertising identifiers, or categories of sensitive personal data.
        </div>
      </SimpleLegalSection>

      <SimpleLegalSection title="3. How We Use Your Data">
        <ul className="list-disc pl-5 space-y-1">
          <li>To create, render, and maintain your profile page;</li>
          <li>To authenticate your identity and secure your account;</li>
          <li>To send required transactional account communications;</li>
          <li>To generate basic analytics using hashed IP counts only;</li>
          <li>To prevent abuse, enforce rate limits, and detect suspicious activity; and</li>
          <li>To monitor and improve the stability and performance of the Service.</li>
        </ul>
      </SimpleLegalSection>

      <SimpleLegalSection title="4. Data Sharing and Disclosure">
        <p>
          We disclose personal data only to the extent necessary to operate the
          Service and as described below.
        </p>
        <h3 className="font-medium text-foreground mt-2">Service Providers</h3>
        <ul className="list-disc pl-5 space-y-1">
          <li>Email delivery providers, for transactional communications only;</li>
          <li>Object storage and content delivery providers, for uploaded media; and</li>
          <li>Managed database hosting providers.</li>
        </ul>
        <h3 className="font-medium text-foreground mt-4">What We Do Not Do</h3>
        <ul className="list-disc pl-5 space-y-1">
          <li>Sell personal data;</li>
          <li>Deploy third-party advertising or tracking scripts; or</li>
          <li>Build profiles of users across other websites.</li>
        </ul>
        <p>
          We may also disclose personal data where required by law, legal
          process, or to protect the rights, property, or safety of Clove.moe,
          our users, or the public.
        </p>
      </SimpleLegalSection>

      <SimpleLegalSection title="5. Data Security">
        <ul className="list-disc pl-5 space-y-1">
          <li>Transport-layer encryption in transit via HTTPS;</li>
          <li>Strict cookie controls, including HTTP-only, secure, and same-site attributes;</li>
          <li>Rate limiting and abuse detection mechanisms;</li>
          <li>Input validation and content sanitization; and</li>
          <li>Internal access governed by the principle of least privilege.</li>
        </ul>
      </SimpleLegalSection>

      <SimpleLegalSection title="6. Your Rights">
        <ul className="list-disc pl-5 space-y-1">
          <li>Request access to or export of your profile data (contact us);</li>
          <li>Update any profile fields at any time;</li>
          <li>Delete your account, which removes all associated data within thirty (30) days; and</li>
          <li>Withdraw consent by deleting your account.</li>
        </ul>
        <div className="rounded-md border bg-muted/40 px-3 py-2 text-xs">
          Account deletion is available under Settings → Delete Account and is
          irreversible.
        </div>
      </SimpleLegalSection>

      <SimpleLegalSection title="7. Data Retention">
        <ul className="list-disc pl-5 space-y-1">
          <li>Account data is retained for the duration of active use;</li>
          <li>Data associated with deleted accounts is purged within thirty (30) days;</li>
          <li>Hashed view analytics are retained for twelve (12) months; and</li>
          <li>Encrypted backups rotate on a thirty (30) day cycle.</li>
        </ul>
      </SimpleLegalSection>

      <SimpleLegalSection title="8. International Data Transfers">
        <p>
          Your personal data may be processed in the United States. By using the
          Service, you consent to the transfer of your personal data to the
          United States and its processing there as necessary to operate the
          Service.
        </p>
      </SimpleLegalSection>

      <SimpleLegalSection title="9. Children's Privacy">
        <p>
          The Service is not directed to children under the age of thirteen
          (13). If we become aware that a child under thirteen (13) has provided
          personal data, we will take reasonable steps to delete such data.
        </p>
      </SimpleLegalSection>

      <SimpleLegalSection title="10. Changes to This Policy">
        <p>
          We may update this Policy from time to time. Material changes will be
          reflected by an updated "Last Updated" date. Your continued use of the
          Service following the effective date constitutes acceptance of the
          revised Policy.
        </p>
      </SimpleLegalSection>

      <SimpleLegalSection title="11. Contact">
        <ul className="list-disc pl-5 space-y-1">
          <li>
            Email:{" "}
            <a
              href="mailto:support@clove.moe"
              className="text-primary hover:underline"
            >
              support@clove.moe
            </a>
          </li>
          <li>
            Discord:{" "}
            <a
              href="https://discord.gg/emrucgSEq2"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              join the server
            </a>
          </li>
        </ul>
      </SimpleLegalSection>
    </SimpleLegalLayout>
  );
}
